import amplifyconfig from './amplifyconfiguration.json';
import { logger } from './utils/logger';

/**
 * Fetch available sessions for the configured event from the Booking API
 *
 * @returns {Promise<Object>} Sessions data or error
 */
export async function fetchSessions() {
  try {
    const apiUrl = process.env.REACT_APP_BOOKING_API_URL;
    const apiKey = process.env.REACT_APP_BOOKING_API_KEY;
    const eventId = process.env.REACT_APP_EVENT_ID;

    if (!apiUrl || !apiKey || !eventId) {
      throw new Error('Booking API configuration missing. Check environment variables.');
    }

    const response = await fetch(`${apiUrl}/events/${eventId}/sessions`, {
      method: 'GET',
      headers: {
        'x-api-key': apiKey,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    // Handle new API response structure: { sessions: [...], customFields: {...} }
    // Also support legacy format where response is just the sessions array
    const sessions = Array.isArray(data) ? data : (data.sessions || []);
    const customFields = Array.isArray(data) ? {} : (data.customFields || {});

    return {
      success: true,
      sessions,
      customFields,
    };
  } catch (error) {
    logger.error('Error fetching sessions:', error);
    return {
      success: false,
      error: 'FETCH_ERROR',
      message: error.message || 'Failed to fetch sessions',
      sessions: [],
      customFields: {},
    };
  }
}

/**
 * Submit registration data to the backend API
 *
 * @param {Object} data - Registration form data
 * @returns {Promise<Object>} API response
 */
export async function submitRegistration(data) {
  try {
    // Get API URL from amplify configuration
    const apiUrl = amplifyconfig.custom?.API?.RegistrationAPI?.endpoint
      || amplifyconfig.API?.REST?.RegistrationAPI?.endpoint
      || process.env.REACT_APP_API_URL
      || window.AMPLIFY_API_URL;

    if (!apiUrl) {
      throw new Error('API URL not configured. Please check amplifyconfiguration.json');
    }

    const response = await fetch(`${apiUrl}/registration`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();

    return {
      ok: true,
      data: responseData,
    };
  } catch (error) {
    logger.error('Error submitting registration:', error);
    return {
      ok: false,
      error: error.message || 'Failed to submit registration',
    };
  }
}

/**
 * Search for a user by email address (for check-in)
 *
 * @param {string} email - Email address to search
 * @returns {Promise<Object>} Search result with user data or error
 */
export async function searchUserByEmail(email) {
  try {
    const apiUrl = amplifyconfig.custom?.API?.RegistrationAPI?.endpoint
      || amplifyconfig.API?.REST?.RegistrationAPI?.endpoint
      || process.env.REACT_APP_API_URL
      || window.AMPLIFY_API_URL;

    if (!apiUrl) {
      throw new Error('API URL not configured');
    }

    const response = await fetch(
      `${apiUrl}/checkin/search?email=${encodeURIComponent(email)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'UNKNOWN_ERROR',
        message: data.message || 'Failed to search for user',
      };
    }

    return data;
  } catch (error) {
    logger.error('Error searching for user:', error);
    return {
      success: false,
      error: 'NETWORK_ERROR',
      message: error.message || 'Network error occurred',
    };
  }
}

/**
 * Autocomplete search for users by email or lastname
 *
 * @param {string} query - Search query (min 2 characters)
 * @returns {Promise<Object>} Search result with suggestions array
 */
export async function searchUsersAutocomplete(query) {
  try {
    const apiUrl = amplifyconfig.custom?.API?.RegistrationAPI?.endpoint
      || amplifyconfig.API?.REST?.RegistrationAPI?.endpoint
      || process.env.REACT_APP_API_URL
      || window.AMPLIFY_API_URL;

    if (!apiUrl) {
      throw new Error('API URL not configured');
    }

    const response = await fetch(
      `${apiUrl}/checkin/search?q=${encodeURIComponent(query)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'UNKNOWN_ERROR',
        message: data.message || 'Failed to search',
        suggestions: [],
      };
    }

    return data;
  } catch (error) {
    logger.error('Error in autocomplete search:', error);
    return {
      success: false,
      error: 'NETWORK_ERROR',
      message: error.message || 'Network error occurred',
      suggestions: [],
    };
  }
}

/**
 * Request a presigned PUT URL for a passport photo (pre-booking).
 * Object lives under tmp/{uploadSessionId}/{slotId} until claimed by the
 * booking POST; abandoned tmp/ uploads are swept by S3 lifecycle after 1d.
 */
export async function requestPassportUploadUrl(payload) {
  const apiUrl = process.env.REACT_APP_BOOKING_API_URL;
  const apiKey = process.env.REACT_APP_BOOKING_API_KEY;
  if (!apiUrl || !apiKey) {
    throw new Error('Booking API configuration missing. Check environment variables.');
  }
  const response = await fetch(`${apiUrl}/public/passport-upload-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || `Upload URL failed (${response.status})`);
  }
  return response.json();
}

/**
 * Request server-side Textract extraction of structured fields from a
 * passport photo already uploaded to S3 tmp/. Returns { fields, confidence }.
 */
export async function requestPassportExtract(s3Key) {
  const apiUrl = process.env.REACT_APP_BOOKING_API_URL;
  const apiKey = process.env.REACT_APP_BOOKING_API_KEY;
  if (!apiUrl || !apiKey) {
    throw new Error('Booking API configuration missing.');
  }
  const response = await fetch(`${apiUrl}/public/passport-extract`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
    body: JSON.stringify({ s3Key }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || `Extract failed (${response.status})`);
  }
  return response.json();
}

/**
 * Create a booking for an event session
 *
 * @param {Object} bookingData - Booking data
 * @param {string} bookingData.eventId - The event ID
 * @param {string} bookingData.email - Attendee email
 * @param {string} bookingData.firstName - Attendee first name
 * @param {string} bookingData.lastName - Attendee last name
 * @param {string} [bookingData.phone] - Attendee phone (optional)
 * @param {string} [bookingData.country] - Attendee country code (optional)
 * @param {string[]} bookingData.sessionIds - Array of session IDs to book
 * @param {Object} bookingData.sessionSelectedDates - Map of sessionId to date (YYYY-MM-DD)
 * @returns {Promise<Object>} Booking result with booking details or error
 */
export async function createBooking(bookingData) {
  try {
    const apiUrl = process.env.REACT_APP_BOOKING_API_URL;
    const apiKey = process.env.REACT_APP_BOOKING_API_KEY;

    if (!apiUrl || !apiKey) {
      throw new Error('Booking API configuration missing. Check environment variables.');
    }

    const response = await fetch(`${apiUrl}/bookings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
      },
      body: JSON.stringify(bookingData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();
    return {
      success: true,
      booking: responseData.booking,
      message: responseData.message,
    };
  } catch (error) {
    logger.error('Error creating booking:', error);
    return {
      success: false,
      error: 'BOOKING_ERROR',
      message: error.message || 'Failed to create booking',
    };
  }
}

/**
 * Record a check-in for a user
 *
 * @param {string} email - Email address of user to check in
 * @returns {Promise<Object>} Check-in result
 */
export async function recordCheckin(email) {
  try {
    const apiUrl = amplifyconfig.custom?.API?.RegistrationAPI?.endpoint
      || amplifyconfig.API?.REST?.RegistrationAPI?.endpoint
      || process.env.REACT_APP_API_URL
      || window.AMPLIFY_API_URL;

    if (!apiUrl) {
      throw new Error('API URL not configured');
    }

    const response = await fetch(`${apiUrl}/checkin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'UNKNOWN_ERROR',
        message: data.message || 'Failed to check in user',
      };
    }

    return data;
  } catch (error) {
    logger.error('Error checking in user:', error);
    return {
      success: false,
      error: 'NETWORK_ERROR',
      message: error.message || 'Network error occurred',
    };
  }
}

/**
 * Fetch booking details for cancellation confirmation page
 *
 * @param {string} cancelToken - 64-character hex cancellation token
 * @returns {Promise<Object>} Booking details or error
 */
export async function getBookingForCancel(cancelToken) {
  try {
    const apiUrl = process.env.REACT_APP_BOOKING_API_URL;
    const apiKey = process.env.REACT_APP_BOOKING_API_KEY;

    if (!apiUrl || !apiKey) {
      throw new Error('Booking API configuration missing. Check environment variables.');
    }

    const response = await fetch(
      `${apiUrl}/bookings/cancel?token=${encodeURIComponent(cancelToken)}`,
      {
        method: 'GET',
        headers: {
          'x-api-key': apiKey,
          'Content-Type': 'application/json',
        },
      }
    );

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        status: response.status,
        message: data.message || 'Failed to fetch booking details',
      };
    }

    return {
      success: true,
      booking: data,
    };
  } catch (error) {
    logger.error('Error fetching booking for cancel:', error);
    return {
      success: false,
      error: 'NETWORK_ERROR',
      message: error.message || 'Network error occurred',
    };
  }
}

/**
 * Cancel a booking using a cancellation token
 *
 * @param {string} cancelToken - 64-character hex cancellation token
 * @returns {Promise<Object>} Cancellation result or error
 */
export async function cancelBooking(cancelToken) {
  try {
    const apiUrl = process.env.REACT_APP_BOOKING_API_URL;
    const apiKey = process.env.REACT_APP_BOOKING_API_KEY;

    if (!apiUrl || !apiKey) {
      throw new Error('Booking API configuration missing. Check environment variables.');
    }

    const response = await fetch(`${apiUrl}/bookings/cancel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
      },
      body: JSON.stringify({ cancelToken }),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        status: response.status,
        message: data.message || 'Failed to cancel booking',
      };
    }

    return {
      success: true,
      bookingId: data.bookingId,
      message: data.message,
    };
  } catch (error) {
    logger.error('Error cancelling booking:', error);
    return {
      success: false,
      error: 'NETWORK_ERROR',
      message: error.message || 'Network error occurred',
    };
  }
}
